import { addToast } from '@medly-components/core';
import { PATH_ID_SEPARATOR, STATION_SIZE } from 'constants/map';
import { ExpandedStation, PathData, PathSelectionOptionValueType, Station } from 'pages/Map/types';
import colors from 'theme/colors';
import { SubZoneStation } from '../Station';
import { getIndicators, loopbackFinder, pathFinder } from './path-points-utils';
import { getPathNodeList, isSamePoint, mergeNodeList } from './path-points-utils/utils';
import { Indicator, Point } from './types';
import { getPathDirection } from './utils';
import { PathNodeCollection } from 'context/ZoneContext';

export const LoopBackPathsColor = {
    selected: {
        pathColor: colors.yellow[50],
        strokePathColor: colors.yellow[500]
    },
    notSelected: {
        pathColor: colors.silverGrey[100],
        strokePathColor: colors.silverGrey[500]
    },
    default: {
        pathColor: colors.royalBlue[50],
        strokePathColor: colors.royalBlue[200]
    }
};

export class Path {
    source: Station;
    target: Station;
    description?: string;
    isLoopBack: boolean;
    isSelected: PathSelectionOptionValueType;
    points: Point[];
    indicators: Indicator[];

    constructor({ source, target, description, isLoopBack, isSelected }: PathData) {
        this.source = source;
        this.target = target;
        this.description = description;
        this.isLoopBack = isLoopBack;
        this.isSelected = isSelected;
        this.points = [];
        this.indicators = [];
    }

    getId() {
        return `${this.source.getId()}${PATH_ID_SEPARATOR}${this.target.getId()}`;
    }

    getDescription() {
        return this.description;
    }

    isLoopBackPath() {
        return this.isLoopBack;
    }

    isSelectedPath() {
        return this.isSelected;
    }

    setIsSelected(value: PathSelectionOptionValueType) {
        this.isSelected = value;
    }

    setPoints(points: Point[]) {
        return (this.points = points);
    }

    setIndicators(indicators: Indicator[]) {
        return (this.indicators = indicators);
    }

    getPoints() {
        return this.points;
    }

    getIndicators() {
        return this.indicators;
    }

    setDrawablePath(
        allStations: Station[],
        pathNodeCollection: PathNodeCollection,
        expandedStation?: ExpandedStation,
        isMapBuilder?: boolean
    ) {
        const nonHiddenStations = allStations.filter(station => !station.isHidden(expandedStation));
        const nonHiddenStationIds = nonHiddenStations.map(station => station.id);
        const source = this.source.getAbsolutePosition();
        const target = this.target.getAbsolutePosition();
        const stations = nonHiddenStations.map(station => station.getAbsolutePosition());

        if (this.getPoints().length > 0 && pathNodeCollection.current.pathIds.indexOf(this.getId()) !== -1) {
            return;
        }

        let path: Point[] = [];
        try {
            path =
                nonHiddenStationIds.includes(this.source.id) &&
                nonHiddenStationIds.includes(this.target.id) &&
                !this.isHidden(expandedStation)
                    ? this.isLoopBackPath()
                        ? loopbackFinder({ source, target, stations, pathNodeList: pathNodeCollection.current.pathNodeList })
                        : pathFinder({
                              source,
                              target,
                              stations,
                              pathNodeList: pathNodeCollection.current.pathNodeList
                          })
                    : [];
        } catch (error) {
            const message = `Not able to find path between ${this.source.getDescription()} and ${this.target.getDescription()}`;
            isMapBuilder && addToast({ variant: 'error', message });
            return;
        }

        if (path.length > 0 && !isSamePoint(target, path[path.length - 1])) {
            const message = `Not able to find path between ${this.source.getDescription()} and ${this.target.getDescription()}`;
            isMapBuilder && addToast({ variant: 'error', message });
            return;
        }

        if (path.length > 0 && pathNodeCollection.current.pathIds.indexOf(this.getId()) === -1) {
            const nodeList = getPathNodeList(path);
            pathNodeCollection.current = {
                pathNodeList: mergeNodeList({ source: nodeList, target: pathNodeCollection.current.pathNodeList }),
                pathIds: [...pathNodeCollection.current.pathIds, this.getId()]
            };
        }

        const indicators = getIndicators(path);
        const points = path.map(({ x, y }) => ({
            x: x + STATION_SIZE / 2,
            y: y + STATION_SIZE / 2
        }));
        this.setPoints(points); //Setting points on path once (re)drawn, so that we can use them anytime later
        this.setIndicators(indicators); //Setting indicators on path once (re)drawn, so that we can use them anytime later
    }

    /**
     * Path will be shown if both of the stations are visible
     * If Subzone is expanded,Subzone is visible but no need of path connecting subzone station to next station
     * In expanded view there will be a path from subzone end station to next station, so main path should be hidden (main path is only intended for zoom level 1)
     */
    isHidden(expandedStation?: ExpandedStation) {
        if (this.source instanceof SubZoneStation) {
            const isPathFromSubZoneParentToChild = this.source.getId() === this.target.getParentStation()?.getId();
            return (this.source.isExpanded(expandedStation) && !isPathFromSubZoneParentToChild) || this.target.isHidden(expandedStation);
        }

        return this.source.isHidden(expandedStation) || this.target.isHidden(expandedStation);
    }

    getPathColor = () => {
        if (this.isLoopBack) {
            return this.isSelected === true
                ? LoopBackPathsColor.selected.pathColor
                : this.isSelected === false
                ? LoopBackPathsColor.notSelected.pathColor
                : LoopBackPathsColor.default.pathColor;
        }

        return this.isSelected === true ? colors.yellow[500] : this.isSelected === false ? colors.silverGrey[500] : colors.royalBlue[200];
    };

    getStrokePathColor = () => {
        if (this.isLoopBack) {
            return this.isSelected === true
                ? LoopBackPathsColor.selected.strokePathColor
                : this.isSelected === false
                ? LoopBackPathsColor.notSelected.strokePathColor
                : LoopBackPathsColor.default.strokePathColor;
        }
    };

    /**
     * To find in which direction path is connecting to source station
     */
    findSourceIntersectionDirection = () => {
        if (!this.points.length) {
            return null;
        }
        return getPathDirection(this.points[0], this.points[1]);
    };

    /**
     * To find in which direction path is connecting to target station
     */
    findTargetIntersectionDirection = () => {
        if (!this.points.length) {
            return null;
        }

        const length = this.points.length;
        return getPathDirection(this.points[length - 1], this.points[length - 2]);
    };
}
